<template>
  <section>
    <validation-observer ref="sampleCSVFileForm" #default="{invalid}">
      <b-form ref="form" @submit.prevent="onCreateOrUpdateSampleCSVFile">
        <b-card class="blog-edit-wrapper">
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Title"
                label-for="title"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Title"
                  rules="required"
                >
                  <b-form-input
                    id="title"
                    v-model="sampleCSVFileData.title"
                    :state="errors.length > 0 ? false : null"
                    :disabled="disableForm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12">
              <b-form-group
                label="Reference"
                label-for="reference"
                class="mb-2"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="reference"
                    rules="required"
                  >
                    <v-select
                      v-model="sampleCSVFileData.reference"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="references"
                      :reduce="val => val.value"
                      clearable
                      label="label"
                      input-id="supported-terms"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Description"
                label-for="description"
                class="mb-2 w-50"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="sampleCSVFileData.description"
                    :state="errors.length > 0 ? false : null"
                    :disabled="disableForm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="{ errors }"
                name="CSV File"
                :rules="{
                  required: false,
                  size: 5000,
                }"
              >
                <b-form-group
                  label=""
                  label-for="sample_file"
                  class="w-50"
                >
                  <b-form-file
                    v-show="true"
                    id="sample_file"
                    ref="sample_file_element"
                    v-model="csvFile"
                    accept=".csv"
                    :placeholder="isUpdate ? updatingSampleCsvFile.file_info.name : 'Choose a CSV file'"
                    :state="errors.length > 0 ? false : null"
                    no-drop
                    :disabled="disableForm"
                    @input="onFileSelected"
                  />
                  <small class="text-danger mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <div class="mt-5 w-100 d-flex justify-content-between mr-2">
              <b-button style="width: 150px" variant="primary" block type="submit" :disabled="invalid || disableForm" class="ml-2">
                {{ isUpdate ? 'Update' : 'Create' }}
              </b-button>
            </div>
          </b-row>
        </b-card>
      </b-form>
    </validation-observer>
  </section>
</template>

<script>
import {
  BCard,
  BMedia,
  BAvatar,
  BCardText,
  BMediaAside,
  BMediaBody,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BImg,
  BFormFile,
  BLink,
  BButton,
  BFormTextarea
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import {
  quillEditor
} from 'vue-quill-editor'
import Ripple from 'vue-ripple-directive'

import {
  required
} from '@validations'
import {
  ValidationProvider,
  ValidationObserver
} from 'vee-validate'

import {
  cloneDeep,
  get
} from "lodash";

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const rawSampleCSVFileData = {
  title: "",
  description: "",
  reference: ""
}
export default {
  components: {
    BCard,
    BMedia,
    BAvatar,
    BCardText,
    BMediaAside,
    BMediaBody,
    BForm,
    BLink,
    BImg,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormTextarea,
    vSelect,
    quillEditor,

    // validations
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    updatingSampleCsvFile: {
      type: [Object, null, undefined],
      default: null,
      required: false
    },
  },
  data() {
    return {
      loading: false,
      sampleCSVFileData: cloneDeep(rawSampleCSVFileData),
      errorMessage: "",
      snowOption: {
        theme: 'snow',
      },
      required,
      csvFile: null,
      references: [
        { label: "Users", value: "users" },
        { label: "Bio Data", value: "bio_data" },
      ],
      sampleFilePath: "",
    }
  },
  computed: {
    isUpdate() {
      return !!this.updatingSampleCsvFile
    },
    canAddNewSampleCSVFiles() {
      return this.can("sample_csv_files:CanAddNew")
    },
    canUpdateSampleCSVFiles() {
      return this.can("sample_csv_files:CanUpdate")
    },
    disableForm() {
      if (get(this.sampleCSVFileData, 'status') === 'deleted') {
        return true
      }
      if (this.updatingSampleCsvFile) {
        return !this.canUpdateSampleCSVFiles
      }
      return !this.canAddNewSampleCSVFiles
    }
  },
  watch: {
    updatingSampleCsvFile: {
      handler(update) {
        if (update) {
          this.sampleCSVFileData = cloneDeep(update);
          this.sampleFilePath = this.getValueFromSource(this.sampleCSVFileData, 'file_info.path');
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async onCreateOrUpdateSampleCSVFile() {
      try {
        this.loading = true;

        const success = await this.$refs.sampleCSVFileForm.validate();
        if (!success) {
          return;
        }

        const formData = new FormData();

        formData.append("title", this.sampleCSVFileData.title);
        formData.append("description", this.sampleCSVFileData.description);
        formData.append("reference", this.sampleCSVFileData.reference);

        if (this.csvFile) {
          formData.append('csv_file', this.csvFile)
        }

        if (this.isUpdate) {
          this.$emit("update", formData)
        } else {
          this.$emit("create", formData)
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    onFileSelected(file) {
      const reader = new FileReader()

      reader.addEventListener(
        'load',
        () => {
          this.sampleFilePath = reader.result
          this.csvFile = file;
        },
        false,
      )

      if (file) {
        reader.readAsDataURL(file)
      }
    },
    resetForm() {
      Object.keys(cloneDeep(this.sampleCSVFileData)).forEach(key => {
        const resetValue = get(rawSampleCSVFileData, key);
        this.sampleCSVFileData[key] = resetValue
        this.$set(this.sampleCSVFileData, resetValue);
      });
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/pages/page-blog.scss';
</style>
